import React, { Component, createContext } from 'react';
import PropTypes from 'prop-types';
import 'core-js/actual';
import 'regenerator-runtime/runtime';
import { Route, Switch } from 'react-router';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import svgxuse from 'svgxuse';
import smoothscroll from 'smoothscroll-polyfill';
import CryptoJS from 'crypto-js';
import { useIntl } from 'react-intl';
import { createBrowserHistory } from 'history';

import EnvConfig from 'env.config';
import PreloadBrands from 'common/app/Preload';
import AppProvider from 'components/AppProvider';
import Notifications from 'components/Common/Notifications';
import ErrorBoundary from 'components/Errors/ErrorBoundary';
import NotFoundError from 'components/Errors/NotFoundError';
import ServerError from 'components/Errors/ServerError';
import InternetConnectionError from 'components/Errors/InternetConnectionError';
import SessionTimeoutError from 'components/Errors/SessionTimeoutError';
import InsufficientFormInformationError from 'components/Errors/InsufficientFormInformationError';
import UnsupportedError from 'components/Errors/UnsupportedError';
import UnauthorisedError from 'components/Errors/UnauthorisedError';
import UnlicensedError from 'components/Errors/UnlicensedError';
import ServiceUnavailableError from 'components/Errors/ServiceUnavailableError';
import SuccessPage from 'components/SuccessPage';
import FooterLinkPage from 'components/Footer/FooterLinkPage';
import LogoutPage from 'components/LogoutPage';
import AuthentaLinkPinConfirmation from 'components/AuthentaLink/PinConfirmation';
import AuthentaLinkReferenceConfirmation from 'components/AuthentaLink/ReferenceConfirmation';
import DefaultFormDisplay from 'components/Forms/DefaultFormDisplay';
import Header from 'components/Header';
import Footer from 'components/Footer';
import CookieBanner from 'components/CookieBanner';
import LoadingWrapper from 'components/Common/LoadingWrapper';
import KeepAlive from 'components/KeepAlive';
import Eula from 'components/Modals/Eula';
import 'components/IconLibrary';

import PreQualifyingQuestionnaire from 'components/Questionnaires/PreQualifyingQuestionnaire';
import FormSelection from 'containers/FormSelection';
import Home from 'containers/Home';
import FormContainer from 'containers/Form';
import FormGroupContainer from 'containers/FormGroup';

import {
  AuthState,
  getSitePreload,
} from 'actions';
import { getOnlineConfig, redirectToApi, getAuthState } from 'actions/app';

import '../../scss/vendors/bootstrap.scss';
import '../../scss/app.global.scss';
import '../../scss/branding/taina.scss';

const history = createBrowserHistory({ basename: process.env.base });
export { history };
export { svgxuse };
export const ConfigContext = createContext(null);

const unprotectedRoutes = [
  '/loggedout',
  'login',
  '/unauthorized',
  '/unlicensed',
  '/server',
  '/notfound',
  '/unsupported',
  '/no-internet',
  '/sessiontimeout',
  '/unavailable',
  '/links/privacy',
  '/links/cookies',
  '/links/termsOfUse',
  '/authentalink',
  '/confirmPin'
];

const confirmAppStateToProceed = [
  '/authentalink'
];

// A wrapper for <Route> that displays loading
// screen if you're not yet authenticated.
const ProtectedRoute = ({ isAllowed, ...props }) => (
  isAllowed ? <Route { ...props } /> : <div className="loading-container"><AppLoadingWrapper /></div>
);

ProtectedRoute.propTypes = {
  isAllowed: PropTypes.bool.isRequired
};

const AppLoadingWrapper = () => {
  const intl = useIntl();

  return (
    <div className="loading-container"><LoadingWrapper text={ intl.formatMessage({ id: 'loading.message' }) } /></div>
  );
};

/* eslint-disable react/prefer-stateless-function */
class App extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isEulaAccepted: PropTypes.bool.isRequired,
    bypassEula: PropTypes.bool.isRequired,
    defaultFormType: PropTypes.string,
    customerName: PropTypes.string,
    authState: PropTypes.string,
    messages: PropTypes.shape({}),
    shortDictionaryLoaded: PropTypes.bool,
    fullDictionaryLoaded: PropTypes.bool,
    onlineConfig: PropTypes.shape(),
    isOnline: PropTypes.bool,
    showModalUpFront: PropTypes.bool,
    router: PropTypes.shape(),
    singleFormJourney: PropTypes.bool,
  };

  static defaultProps = {
    defaultFormType: null,
    customerName: null,
    authState: AuthState.Unknown,
    messages: null,
    shortDictionaryLoaded: false,
    fullDictionaryLoaded: false,

    onlineConfig: null,
    isOnline: false,
    showModalUpFront: false,
    router: null,
    singleFormJourney: false,
  };

  static Brand = process.env.brand;

  static Basename = process.env.base;

  static hasAppCookies() {
    if (document.cookie && document.cookie.split(';').filter(item => item.trim().startsWith(EnvConfig.cookieConsentName)).length) {
      return true;
    }

    return false;
  }

  static isFormPage() {
    return window.location.pathname.includes('/forms');
  }

  static isUadLink = () => window.location.pathname.startsWith(`${ App.Basename }uad/`);

  componentDidMount() {
    const { dispatch } = this.props;

    smoothscroll.polyfill();

    if (App.isUadLink()) {
      let path = window.location.pathname;
      if (App.Basename !== '/') {
        path = path.replace(App.Basename, '/');
      }
      redirectToApi(path);
    }

    if (!unprotectedRoutes.includes(window.location.pathname) && !App.isUadLink()) {
      if (PreloadBrands.includes(App.Brand)) {
        this.preloadCheck();
      } else {
        dispatch(getOnlineConfig());
      }
    }

    if (confirmAppStateToProceed.includes(window.location.pathname)) {
      dispatch(getAuthState());
    }
  }

  get hasDefaultFormType() {
    const { defaultFormType } = this.props;
    return defaultFormType !== null;
  }

  get canAccessMainRoutes() {
    const { isEulaAccepted, bypassEula } = this.props;
    return (!App.isUadLink() && (bypassEula || isEulaAccepted || unprotectedRoutes.includes(window.location.pathname)));
  }

  async preloadCheck() {
    const { dispatch } = this.props;
    try {
      const response = await fetch('config/preload.json');
      if (!response.ok) {
        if (response.status === 404) {
          dispatch(getOnlineConfig());
        } else {
          throw new Error(`Unexpected error getting preload.json. Status: ${ response.status }`);
        }
      }
      const jsonData = await response.json();
      dispatch(getSitePreload(jsonData));
    } catch (ex) {
      console.error('Unexpected error getting preload.json', ex);
    }
  }

  render() {
    const { isAuthenticated, isEulaAccepted, customerName, authState, messages, shortDictionaryLoaded, fullDictionaryLoaded, isOnline, onlineConfig, showModalUpFront, singleFormJourney, router } = this.props;

    const showMenu = isEulaAccepted && !(router.location.pathname === '/insufficient-information' && singleFormJourney);

    const showContent = messages && ((!isOnline && shortDictionaryLoaded) || (isOnline && fullDictionaryLoaded));

    return (
      <ErrorBoundary origin="App">
        <ConfigContext.Provider value={ onlineConfig }>
          <ConnectedRouter basename={ App.Basename } history={ history }>
            <AppProvider>
              <Link className="skip-nav" to="#main">Skip to main content</Link>
              <Header showMenu={ showMenu } customerName={ customerName } />
              <main id="main" className={ isAuthenticated ? 'auth' : '' }>
                { !showContent && <AppLoadingWrapper /> }
                { showContent && <React.Fragment>
                  <div className="container" aria-live="polite">
                    <Notifications />
                  </div>
                  { this.canAccessMainRoutes && <div id="main-container" className="container main-content">
                    <ErrorBoundary origin="Page">
                      <KeepAlive unprotectedRoutes={ unprotectedRoutes } />
                      { this.hasDefaultFormType && !App.isFormPage() && <DefaultFormDisplay /> }
                      { /* showModalUpFront should prevent rendering of the below components only when it is null.
                       This is to allow an authentalink with defaultform to go straight to its form without any
                       flickering render beforehand.
                       Other single form cases come through with defaultform on state but should render the modal. These
                       have showModalUpFront:true and must allow the app elements to render underneath. When the
                       modal gets closed, showModalUpFront gets set to false and here too the app components must render */ }
                      <Switch>
                        <ProtectedRoute isAllowed={ isAuthenticated && !(this.hasDefaultFormType && showModalUpFront === null) } exact path="/" component={ Home } />
                        <ProtectedRoute isAllowed={ isAuthenticated && !(this.hasDefaultFormType && showModalUpFront === null) } exact path="/form-selection" component={ FormSelection } />
                        <ProtectedRoute isAllowed={ isAuthenticated } exact path="/forms/groups/:formGroupType/:groupId" component={ FormGroupContainer } />
                        <ProtectedRoute isAllowed={ isAuthenticated } exact path="/forms/groups/:formGroupType/:groupId/success" component={ SuccessPage } />
                        <ProtectedRoute isAllowed={ isAuthenticated } exact path="/forms/:formType/:formId" component={ FormContainer } />
                        <ProtectedRoute isAllowed={ isAuthenticated } exact path="/forms/:formType/:formId/success" component={ SuccessPage } />
                        <ProtectedRoute isAllowed={ isAuthenticated && !(this.hasDefaultFormType && showModalUpFront === null) } exact path="/pre-qualifying-questionnaire" component={ PreQualifyingQuestionnaire } />
                        <Route exact path="/loggedout" component={ LogoutPage } />
                        <Route exact path="/unauthorized" component={ UnauthorisedError } />
                        <Route exact path="/unlicensed" component={ UnlicensedError } />
                        <Route exact path="/links/:pageType" component={ FooterLinkPage } />
                        { /* <Route exact path="/style-guide" component={ StyleGuide } /> */ }
                        <Route exact path="/no-internet" component={ InternetConnectionError } />
                        <Route exact path="/unsupported" component={ UnsupportedError } />
                        <Route exact path="/server" component={ ServerError } />
                        <Route exact path="/sessiontimeout" component={ SessionTimeoutError } />
                        <Route exact path="/insufficient-information" component={ InsufficientFormInformationError } />
                        <Route exact path="/unavailable" component={ ServiceUnavailableError } />
                        <ProtectedRoute isAllowed={ authState !== AuthState.Unknown && authState !== AuthState.Requesting } exact path="/authentalink" component={ AuthentaLinkReferenceConfirmation } />
                        <Route exact path="/confirmPin" component={ AuthentaLinkPinConfirmation } />
                        <Route component={ NotFoundError } />
                      </Switch>
                    </ErrorBoundary>
                  </div> }
                  { !App.hasAppCookies() && <CookieBanner /> }
                  { isAuthenticated && !isEulaAccepted && !unprotectedRoutes.includes(window.location.pathname) && <Eula /> }
                  { (App.isUadLink() || (!isAuthenticated && !isEulaAccepted && !unprotectedRoutes.includes(window.location.pathname))) && <AppLoadingWrapper /> }
                </React.Fragment> }
              </main>
              { this.canAccessMainRoutes && <Footer /> }
            </AppProvider>
          </ConnectedRouter>
        </ConfigContext.Provider>
      </ErrorBoundary>
    );
  }
}

const mapDispatchToProps = dispatch => ({ dispatch });

function mapStateToProps(state) {
  const {
    app: {
      isAuthenticated,
      isEulaAccepted,
      bypassEula,
      onlineConfig,
      authState,
      customerData,
      key,
      messages,
      shortDictionaryLoaded,
      fullDictionaryLoaded
    },
    router,
  } = state;

  let customerDetails = null;

  if (customerData && key) {
    const decryptedArray = CryptoJS.AES.decrypt(customerData, key);
    const decryptedText = decryptedArray.toString(CryptoJS.enc.Utf8);
    customerDetails = JSON.parse(decryptedText);
  }

  const activeFormTypes = onlineConfig?.formTypes?.filter(x => x.isEnabled && x.isVisible) ?? [];
  const activeFormGroups = onlineConfig?.formGroups?.filter(x => x.isEnabled && x.isVisible) ?? [];
  const singleFormJourney = activeFormTypes.length + activeFormGroups.length === 1;

  return {
    isAuthenticated,
    isEulaAccepted,
    bypassEula,
    defaultFormType: onlineConfig && onlineConfig.defaultFormType && onlineConfig.defaultFormType.toLowerCase(),
    customerName: customerDetails && customerDetails.CustomerName,
    authState,
    messages,
    shortDictionaryLoaded,
    fullDictionaryLoaded,
    isOnline: onlineConfig !== null,
    showModalUpFront: (onlineConfig && onlineConfig.showFormInstructionsUpFront),
    router,
    singleFormJourney
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(App);
